import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, deleteDoc, doc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../../services/firebase';

interface ViewGalleryProps {
  domain: string;
}

interface GalleryPhoto {
  id: string;
  imageUrl: string;
  fileName: string;
  originalName: string;
  className: string;
  section: string;
  academicYear: string;
  enrollmentID: string;
  uploadedAt: Date;
  size: number;
}

const CLASSES = ['All Classes', ...Array.from({ length: 12 }, (_, i) => `Class ${i + 1}`)];
const SECTIONS = ['All Sections', 'A', 'B', 'C', 'D', 'E'];

function generateAcademicYears(): string[] {
  const currentYear = new Date().getFullYear();
  const years: string[] = [];
  for (let y = currentYear - 2; y <= currentYear + 1; y++) {
    years.push(`${y}-${y + 1}`);
  }
  return years;
}

const ACADEMIC_YEARS = ['All Years', ...generateAcademicYears()];

const ViewGallery: React.FC<ViewGalleryProps> = ({ domain }) => {
  const [photos, setPhotos] = useState<GalleryPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterClass, setFilterClass] = useState('All Classes');
  const [filterSection, setFilterSection] = useState('All Sections');
  const [filterYear, setFilterYear] = useState('All Years');
  const [filterEnrollment, setFilterEnrollment] = useState('');
  const [lightboxPhoto, setLightboxPhoto] = useState<GalleryPhoto | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    if (!domain) return;

    const colRef = collection(db, 'school-users', domain, 'photo-gallery');
    let q = query(colRef, orderBy('uploadedAt', 'desc'));

    // Apply filters via client-side filtering since Firestore compound queries
    // would require composite indexes for each combination
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allPhotos: GalleryPhoto[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          imageUrl: data.imageUrl,
          fileName: data.fileName,
          originalName: data.originalName,
          className: data.className,
          section: data.section || '',
          academicYear: data.academicYear,
          enrollmentID: data.enrollmentID || '',
          uploadedAt: data.uploadedAt?.toDate?.() || new Date(),
          size: data.size,
        };
      });
      setPhotos(allPhotos);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [domain]);

  const filteredPhotos = photos.filter((p) => {
    if (filterClass !== 'All Classes' && p.className !== filterClass) return false;
    if (filterSection !== 'All Sections' && p.section !== filterSection) return false;
    if (filterYear !== 'All Years' && p.academicYear !== filterYear) return false;
    if (filterEnrollment && !p.enrollmentID.toLowerCase().includes(filterEnrollment.toLowerCase())) return false;
    return true;
  });

  const handleDelete = async (photo: GalleryPhoto) => {
    if (!confirm(`Delete "${photo.originalName}"?`)) return;

    setDeleting(photo.id);
    try {
      // Delete from Storage
      const storagePath = `${domain}/photo-gallery/${photo.academicYear}/${photo.className}/${photo.section}/${photo.fileName}`;
      const storageRef = ref(storage, storagePath);
      await deleteObject(storageRef);

      // Delete from Firestore
      await deleteDoc(doc(db, 'school-users', domain, 'photo-gallery', photo.id));

      if (lightboxPhoto?.id === photo.id) setLightboxPhoto(null);
    } catch (error: any) {
      console.error('Delete error:', error);
      alert('Failed to delete photo: ' + error.message);
    }
    setDeleting(null);
  };

  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div>
      {/* Filters */}
      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-2">
              <label className="form-label fw-semibold">Class</label>
              <select
                className="form-select"
                value={filterClass}
                onChange={(e) => setFilterClass(e.target.value)}
              >
                {CLASSES.map((cls) => (
                  <option key={cls} value={cls}>{cls}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">Section</label>
              <select
                className="form-select"
                value={filterSection}
                onChange={(e) => setFilterSection(e.target.value)}
              >
                {SECTIONS.map((sec) => (
                  <option key={sec} value={sec}>{sec}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label fw-semibold">Academic Year</label>
              <select
                className="form-select"
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
              >
                {ACADEMIC_YEARS.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold">Enrollment ID</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search by Enrollment ID"
                value={filterEnrollment}
                onChange={(e) => setFilterEnrollment(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <span className="badge bg-primary-custom fs-6 px-3 py-2">
                {filteredPhotos.length} photo(s)
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Gallery Grid */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary-custom" role="status"></div>
          <p className="mt-2 text-muted">Loading photos...</p>
        </div>
      ) : filteredPhotos.length === 0 ? (
        <div className="text-center py-5">
          <i className="bi bi-images text-muted" style={{ fontSize: 48 }}></i>
          <p className="mt-3 text-muted">No photos found. Upload some photos to get started!</p>
        </div>
      ) : (
        <div className="row g-3">
          {filteredPhotos.map((photo) => (
            <div key={photo.id} className="col-6 col-md-4 col-lg-3">
              <div className="card border-0 shadow-sm h-100">
                <img
                  src={photo.imageUrl}
                  alt={photo.originalName}
                  className="card-img-top"
                  style={{ height: 180, objectFit: 'cover', cursor: 'pointer' }}
                  onClick={() => setLightboxPhoto(photo)}
                />
                <div className="card-body p-2">
                  <small className="text-truncate d-block fw-semibold" title={photo.originalName}>
                    {photo.originalName}
                  </small>
                  <small className="text-muted d-block">
                    {photo.className}{photo.section ? ` - ${photo.section}` : ''} | {photo.academicYear}
                  </small>
                  {photo.enrollmentID && (
                    <small className="text-primary-custom d-block">
                      <i className="bi bi-person-badge me-1"></i>{photo.enrollmentID}
                    </small>
                  )}
                  <div className="d-flex justify-content-between align-items-center mt-1">
                    <small className="text-muted">{formatSize(photo.size)}</small>
                    <button
                      className="btn btn-outline-danger btn-sm py-0 px-1"
                      onClick={() => handleDelete(photo)}
                      disabled={deleting === photo.id}
                      title="Delete photo"
                    >
                      {deleting === photo.id ? (
                        <span className="spinner-border spinner-border-sm"></span>
                      ) : (
                        <i className="bi bi-trash"></i>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxPhoto && (
        <div
          className="modal d-block"
          style={{ background: 'rgba(0,0,0,0.85)' }}
          onClick={() => setLightboxPhoto(null)}
        >
          <div className="modal-dialog modal-xl modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content bg-dark border-0">
              <div className="modal-header border-0 pb-0">
                <div>
                  <h6 className="text-white mb-0">{lightboxPhoto.originalName}</h6>
                  <small className="text-light">
                    {lightboxPhoto.className}{lightboxPhoto.section ? ` - ${lightboxPhoto.section}` : ''} | {lightboxPhoto.academicYear} | {lightboxPhoto.uploadedAt.toLocaleDateString('en-IN')}
                    {lightboxPhoto.enrollmentID && <> | Enrollment: {lightboxPhoto.enrollmentID}</>}
                  </small>
                </div>
                <button
                  className="btn btn-outline-light btn-sm"
                  onClick={() => setLightboxPhoto(null)}
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
              <div className="modal-body text-center p-2">
                <img
                  src={lightboxPhoto.imageUrl}
                  alt={lightboxPhoto.originalName}
                  style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }}
                />
              </div>
              <div className="modal-footer border-0 pt-0 justify-content-between">
                <a
                  href={lightboxPhoto.imageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-light btn-sm"
                >
                  <i className="bi bi-box-arrow-up-right me-1"></i>Open Original
                </a>
                <button
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => handleDelete(lightboxPhoto)}
                  disabled={deleting === lightboxPhoto.id}
                >
                  <i className="bi bi-trash me-1"></i>Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewGallery;
